/* eslint-disable react/require-default-props */
import React, { forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import styles from '../styles/uikitMenusMenuList.module.scss';

function nextItem(list, item) {
  if (list === item) {
    return list.firstChild;
  }

  if (item && item.nextElementSibling) {
    return item.nextElementSibling;
  }

  return list.firstChild;
}

function previousItem(list, item) {
  if (list === item) {
    return list.lastChild;
  }

  if (item && item.previousElementSibling) {
    return item.previousElementSibling;
  }

  return list.lastChild;
}

function controlFocus(list, currentFocus, changeItem) {
  let nextFocus = changeItem(list, currentFocus);

  while (nextFocus) {
    if (!nextFocus.hasAttribute('tabindex')) {
      nextFocus = changeItem(list, nextFocus);
    } else {
      nextFocus.focus();
      return true;
    }
  }

  return false;
}

const MenuList = forwardRef(
  (
    {
      className = '',
      children,
      onBlur = null,
      onKeyDown = null,
      shouldSetFocus = true,
      testId = '',
    },
    ref,
  ) => {
    const handleOnKeyDown = (event) => {
      const list = ref.current;
      const { key } = event;
      const currentFocus = list.ownerDocument.activeElement;

      if (key === 'ArrowDown') {
        event.preventDefault();
        controlFocus(list, currentFocus, nextItem);
      } else if (key === 'ArrowUp') {
        event.preventDefault();
        controlFocus(list, currentFocus, previousItem);
      } else if (key === 'Home') {
        event.preventDefault();
        controlFocus(list, null, nextItem);
      } else if (key === 'End') {
        event.preventDefault();
        controlFocus(list, null, previousItem);
      } else if (key === 'Enter') {
        event.preventDefault();
        // check the presence of an anchor tag within the menu list item
        // NOTE: This, along with the `controlFocus` logic, will need to
        // be reworked if we ever add other elements within the dropdown,
        // eg. input, textarea, etc
        if (currentFocus.children.length === 1 && currentFocus.children[0].tagName === 'A') {
          currentFocus.children[0].click();
        } else {
          currentFocus.click();
        }
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    };

    useEffect(() => {
      if (shouldSetFocus) {
        ref.current?.children[0].focus();
      }
    }, [ref, shouldSetFocus]);

    return (
      <ul
        className={classnames(styles.element, className)}
        data-testid={testId}
        onBlur={onBlur}
        onKeyDown={handleOnKeyDown}
        role="menu"
        ref={ref}
      >
        {children}
      </ul>
    );
  },
);

MenuList.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  shouldSetFocus: PropTypes.bool,
  testId: PropTypes.string,
};

export { MenuList as MenuListStorybook };
export default React.memo(MenuList);
